import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Fade,
  useTheme,
} from "@mui/material";
import { CheckCircle, Error, Receipt } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { api, endpoints } from "../../utils/axiosInstance";

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(3),
  maxWidth: 600,
  margin: "0 auto",
  marginTop: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[8],
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: theme.palette.primary.main,
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: theme.spacing(2),
  "& svg": {
    fontSize: 48,
  },
}));

const InfoBox = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

const PaymentSuccess = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handlePaymentSuccess = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const orderCode = params.get("orderCode");

        if (!orderCode) {
          setError("Không tìm thấy mã đơn hàng");
          setLoading(false);
          return;
        }

        // 1. Kiểm tra trạng thái thanh toán từ PayOS
        const checkStatusResponse = await api.get(
          endpoints.payment.checkStatus(orderCode)
        );

        // Sửa lại cách kiểm tra response
        if (!checkStatusResponse) {
          throw new Error("Không thể kiểm tra trạng thái thanh toán");
        }

        const paymentStatus = checkStatusResponse.data;

        // Nếu thanh toán thành công
        if (
          paymentStatus.status === "PAID" ||
          paymentStatus.status === "COMPLETED"
        ) {
          try {
            // Cập nhật trạng thái trong transaction history
            const updateResponse = await api.put(
              endpoints.transactionHistory.updateStatus(orderCode),
              {
                status: "COMPLETED",
                reason: "Thanh toán thành công",
              }
            );

            if (!updateResponse) {
              throw new Error("Không thể cập nhật trạng thái giao dịch");
            }

            // Tạo order mới
            const createOrderResponse = await api.post(
              endpoints.orders.create,
              {
                subscriptionId: updateResponse.data.subscriptionId._id,
                orderCode: orderCode,
                amount: paymentStatus.amount,
              }
            );
            if (!createOrderResponse) {
              throw new Error("Không thể tạo đơn hàng");
            }
          } catch (error) {
            setError(
              "Thanh toán thành công nhưng không thể tạo đơn hàng. Vui lòng liên hệ hỗ trợ."
            );
            setLoading(false);
            return;
          }
        }

        setPaymentData(paymentStatus);
        setLoading(false);
      } catch (error) {
        console.error("Error handling successful payment:", error);
        setError(
          error.response?.data?.message || "Có lỗi xảy ra khi xử lý thanh toán"
        );
        setLoading(false);
      }
    };

    if (location.search) {
      handlePaymentSuccess();
    } else {
      setLoading(false);
    }
  }, [location.search]);

  const handleBackToHome = () => {
    navigate("/");
  };

  const handleTryAgain = () => {
    navigate("/payment");
  };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Fade in={true}>
          <StyledPaper elevation={3}>
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress size={60} thickness={4} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Đang xác minh thanh toán...
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Vui lòng đợi trong giây lát
              </Typography>
            </Box>
          </StyledPaper>
        </Fade>
      </Container>
    );
  }

  if (error || !paymentData) {
    return (
      <Container maxWidth="sm">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <StyledPaper elevation={3}>
            <IconWrapper sx={{ bgcolor: theme.palette.error.light }}>
              <Error color="error" />
            </IconWrapper>
            <Typography variant="h4" gutterBottom align="center">
              Có lỗi xảy ra
            </Typography>
            <Alert severity="error" sx={{ width: "100%" }}>
              {error || "Không thể xác minh trạng thái thanh toán"}
            </Alert>
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleTryAgain}
                startIcon={<Receipt />}
              >
                Thử lại
              </Button>
              <Button variant="outlined" onClick={handleBackToHome}>
                Về trang chủ
              </Button>
            </Box>
          </StyledPaper>
        </motion.div>
      </Container>
    );
  }

  const isPaid =
    paymentData.status === "PAID" || paymentData.status === "COMPLETED";

  return (
    <Container maxWidth="sm">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <StyledPaper elevation={3}>
          <IconWrapper
            sx={{
              bgcolor: isPaid
                ? theme.palette.success.light
                : theme.palette.warning.light,
            }}
          >
            {isPaid ? (
              <CheckCircle sx={{ color: theme.palette.success.main }} />
            ) : (
              <Error sx={{ color: theme.palette.warning.main }} />
            )}
          </IconWrapper>

          <Typography
            variant="h4"
            gutterBottom
            align="center"
            sx={{ fontWeight: 600 }}
          >
            {isPaid ? "Thanh toán thành công!" : "Thanh toán chưa hoàn tất"}
          </Typography>

          <Alert
            severity={isPaid ? "success" : "warning"}
            sx={{ width: "100%" }}
          >
            {isPaid
              ? "Cảm ơn bạn đã sử dụng dịch vụ của chúng tôi. Đơn hàng của bạn đã được xác nhận."
              : "Đơn hàng của bạn chưa được thanh toán hoặc đang trong quá trình xử lý."}
          </Alert>

          <InfoBox>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Mã đơn hàng
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {paymentData.orderCode}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Số tiền thanh toán
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: theme.palette.primary.main }}
              >
                {new Intl.NumberFormat("vi-VN", {
                  style: "currency",
                  currency: "VND",
                }).format(paymentData.amount)}
              </Typography>
            </Box>
          </InfoBox>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 2,
              width: "100%",
              justifyContent: "center",
            }}
          >
            {isPaid ? (
              <>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleBackToHome}
                  sx={{
                    minWidth: 160,
                    borderRadius: theme.shape.borderRadius * 1.5,
                  }}
                >
                  Về trang chủ
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleTryAgain}
                  startIcon={<Receipt />}
                  sx={{
                    minWidth: 160,
                    borderRadius: theme.shape.borderRadius * 1.5,
                  }}
                >
                  Thử lại
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleBackToHome}
                  sx={{
                    minWidth: 160,
                    borderRadius: theme.shape.borderRadius * 1.5,
                  }}
                >
                  Về trang chủ
                </Button>
              </>
            )}
          </Box>
        </StyledPaper>
      </motion.div>
    </Container>
  );
};

export default PaymentSuccess;
